import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  CommunicationService,
  MessageEntityTypesEnum,
  TenantService,
} from "@propertelligent/client-api";
import "./SingleChatmessages.css";
import apiHelper from "../../utils/apiHelper";
import { handleApiErrors } from "../../utils/handleApiErrors";
import SimpleSpinner from "../../components/loader";
import DateHelper, { formatChatDate } from "../../utils/DateHelper";
import { UserLoginData } from "../../components/common/charts/Enums";

interface SingleChatMessagesProps {
  tenantId?: number;
  isHeader?: boolean;
  tenantHeaderChatHeight?: string;
  skipTenantOrgLookup?: boolean;
}

const SingleChatMessages: React.FC<SingleChatMessagesProps> = ({
  tenantId,
  isHeader = true,
  tenantHeaderChatHeight,
  skipTenantOrgLookup = false,
}) => {
  const { id } = useParams<{ id: string }>();
  const [chatEntityId, setChatEntityId] = useState<number | undefined>(
    undefined
  );
  const [chatEntityTypeId] = useState<number | undefined>(
    MessageEntityTypesEnum.Tenant
  );
  const [chatName, setChatName] = useState<string>("");
  const [messageData, setMessageData] = useState({
    messageData: "",
    entityTypeId: MessageEntityTypesEnum.Tenant,
    entityId: undefined,
  });
  const [chatData, setChatData] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Determine tenant ID from props or URL params
  const effectiveTenantId = tenantId || parseInt(id);

  // Fetch tenant details
  useEffect(() => {
    if (effectiveTenantId) {
      setChatEntityId(effectiveTenantId);
      setMessageData((prev) => ({ ...prev, entityId: effectiveTenantId }));

      if (!skipTenantOrgLookup) {
        apiHelper(
          TenantService.getApiTenantOrganizationlookup,
          { showNotification: false },
          parseInt(
            sessionStorage.getItem(UserLoginData.organization_id) || "0"
          ),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          `Id.Equals(${effectiveTenantId})`
        )
          .then((res) => {
            const tenant = res?.data?.success?.[0];
            if (tenant) {
              setChatName(tenant.name);
            }
          })
          .catch((error) => {
            console.error("Error fetching tenant details:", error);
          });
      }
    }
  }, [effectiveTenantId, skipTenantOrgLookup]);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = () => {
      if (!chatEntityId || !chatEntityTypeId) return;

      apiHelper(
        CommunicationService.getApiMessageTenantlist,
        { showNotification: false },
        chatEntityId
      )
        .then((res) => {
          setChatData(res?.data?.success || []);
        })
        .catch((error) => {
          console.error("Error fetching messages:", error);
        })
        .finally(() => {
          setMessagesLoading(false);
        });
    };

    fetchMessages();
  }, [chatEntityId, chatEntityTypeId]);

  // Auto-scroll the message list itself to the latest message, directly via scrollTop rather
  // than scrollIntoView -- scrollIntoView walks every scrollable ancestor (including the page's
  // own .tabs-body tab container) to bring the target into view, which is what caused the whole
  // page to jump on every send; setting this container's own scrollTop can never affect anything
  // outside it, which is what we actually want (only the chat panel moves).
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatData]);

  // Send message
  const handleSend = () => {
    if (!messageData?.messageData?.trim()) return;

    apiHelper(
      CommunicationService.postApiMessage,
      { showNotification: false },
      messageData
    )
      .then((res) => {
        if (res?.data?.success) {
          setMessageData({
            ...messageData,
            messageData: "",
            entityTypeId: chatEntityTypeId,
            entityId: chatEntityId,
          });
          // Refresh messages
          apiHelper(
            CommunicationService.getApiMessageTenantlist,
            { showNotification: false },
            chatEntityId
          )
            .then((res) => {
              setChatData(res?.data?.success || []);
            })
            .catch((error) => {
              console.error("Error refreshing messages:", error);
            });
          return;
        }
        // Fail-closed PhoneNumber (and other field) errors — toast via PubSub.
        if (res?.errors) {
          handleApiErrors(res.errors, () => {});
        }
      })
      .catch((error) => {
        console.error("Error sending message:", error);
      });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  // Render message with URL detection
  const renderMessage = (message: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return message.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--Primary-Regular)", textDecoration: "underline" }}
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  return (
    <div className="messages-container">
      {isHeader && (
        <div className="messages-header">
          <h2 className="messages-header-title">{"Messages"}</h2>
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className={`${
          tenantHeaderChatHeight ? tenantHeaderChatHeight : "messages-content"
        }`}
      >
        {messagesLoading ? (
          <SimpleSpinner myClassName="Icon40" isFullPage={true} />
        ) : chatData.length === 0 ? (
          <div className="message-item">
            <div className="message-text">Be the first to message</div>
          </div>
        ) : (
          chatData.map((item) => {
            const isCurrentUser =
              item.createdById ===
              parseInt(
                sessionStorage.getItem("actualUserId") ||
                  sessionStorage.getItem(UserLoginData.user_id) ||
                  "0"
              );
            return (
              <div
                key={item.id}
                className={`message-item ${
                  isCurrentUser ? "right-message" : "left-message"
                }`}
              >
                <div
                  className={isCurrentUser ? "message-bubble" : "message-text"}
                >
                  {renderMessage(item.messageData)}
                </div>
                <div className="message-timestamp">
                  {new Date(item?.created).toLocaleString()}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="message-input-container">
        <input
          type="text"
          className="message-input"
          placeholder="Type Your Message Here...."
          value={messageData.messageData}
          onChange={(e) =>
            setMessageData({ ...messageData, messageData: e.target.value })
          }
          onKeyPress={handleKeyPress}
        />
        <button className="send-button" onClick={handleSend}>
          Send
        </button>
      </div>
    </div>
  );
};

export default SingleChatMessages;
